import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MuiMaterials } from "../../@index.js";
import { createWindowChromeTheme, getWindowChromeThemeColor } from "./windowChromeTheme.js";
import { outlinedActionButtonSx } from "./actionButtons.js";
import SiteWindowTitle from "./SiteWindowTitle.js";
import SiteWindowHeaderLeading, { SiteWindowToolNav } from "./SiteWindowHeaderLeading.js";
import ChromeHeaderBar from "./ChromeHeaderBar.js";

/**
 * Full-page window chrome shared by every main-menu window. Provides:
 *   - a themed ThemeProvider so children inherit the Site Setup palette
 *   - a fixed-inset overlay in `pageBg`
 *   - a header row with title/subtitle on the left and a Close icon on the right
 *   - an optional action strip under the header (buttons/selects), matching Cable Health stats
 *
 * Body content is rendered as children below the header, inside a scrollable flex column so
 * the chrome itself never scrolls off screen. Windows that need a custom body wrapper (e.g. a
 * 3D canvas that draws behind everything) can set `bodyPadding=0` and take over the flex child.
 *
 * The header re-renders whenever `themeChanged` fires so light/dark toggles update in place
 * without every window having to install its own listener.
 */
export default function WindowChrome({
    title,
    /** Short tools-menu label (e.g. "Setup"). Defaults to `title`. */
    toolTitle = null,
    subtitle = null,
    actions = null,
    onClose,
    closeAriaLabel = "Close",
    children,
    /** Optional status banner (mirrors Site Setup's mustard-tinted banner). */
    statusBanner = null,
    /** Optional loading overlay to render on top of the chrome (leave null when handling it inline). */
    loadingOverlay = null,
    /** Class on the outer overlay div so existing global CSS keeps working. */
    className,
    /** When set, the title becomes a Site XXXX switcher menu. */
    siteWindowId = null,
    /** Current site GUID to carry into the next Site XXXX window. */
    siteGuid = null,
    showHeaderSiteSelect = false,
    onHeaderSiteChange = null,
    headerSites = null,
    headerFilterSite = null,
    headerSiteSelectDisabled = false,
    afterTool = null,
}) {
    const navigate = useNavigate();
    const [themeVersion, setThemeVersion] = useState(0);

    useEffect(() => {
        const onTheme = () => setThemeVersion((prev) => prev + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    const { textPrimary, textSecondary, paper, border, pageBg, theme } = useMemo(
        () => createWindowChromeTheme(),
        // themeVersion is the trigger; createWindowChromeTheme reads global.gw.theme so a
        // themeChanged event bumping the counter recomputes the palette/theme in-place.
        [themeVersion],
    );

    const handleClose = onClose ?? (() => navigate("/sites", { replace: true }));
    const resolvedToolTitle = toolTitle || title;
    const useSiteToolHeader = !!siteWindowId;
    const showSite = useSiteToolHeader
        && !!showHeaderSiteSelect
        && typeof onHeaderSiteChange === "function";

    const leading = useSiteToolHeader ? (
        showSite ? (
            <SiteWindowHeaderLeading
                toolTitle={resolvedToolTitle}
                siteWindowId={siteWindowId}
                siteGuid={siteGuid}
                textSecondary={textSecondary}
                border={border}
                showSiteSelect
                siteSelectValue={siteGuid || ""}
                onSiteChange={onHeaderSiteChange}
                sites={headerSites}
                filterSite={headerFilterSite}
                siteSelectDisabled={headerSiteSelectDisabled}
                includeTool={false}
            />
        ) : null
    ) : (
        <SiteWindowTitle
            title={title}
            subtitle={subtitle}
            siteWindowId={siteWindowId}
            siteGuid={siteGuid}
            textSecondary={textSecondary}
        />
    );

    const tool = useSiteToolHeader ? (
        <SiteWindowToolNav
            toolTitle={resolvedToolTitle}
            siteWindowId={siteWindowId}
            siteGuid={siteGuid}
            textSecondary={textSecondary}
        />
    ) : null;

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                className={className}
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                    // Keep the overlay in the visual viewport (not under the status bar).
                    paddingTop: "env(safe-area-inset-top, 0px)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                <ChromeHeaderBar
                    leading={leading}
                    tool={tool}
                    afterTool={afterTool}
                    actions={actions}
                    onClose={handleClose}
                    closeAriaLabel={closeAriaLabel}
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                />

                {statusBanner}

                <MuiMaterials.Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        position: "relative",
                        backgroundColor: paper,
                        color: textPrimary,
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    {loadingOverlay}
                    {children}
                </MuiMaterials.Box>
            </div>
        </MuiMaterials.ThemeProvider>
    );
}

/**
 * Standard Site-Setup-style status banner. Rendered by callers who want the same mustard box
 * without wiring their own colors.
 */
export function WindowChromeStatusBanner({ status, onDismiss }) {
    if (!status) return null;
    const border = getWindowChromeThemeColor("#cccccc", "#f4cc8e4d");
    const textSecondary = getWindowChromeThemeColor("#666666", "#c4a574");
    const bannerBg = getWindowChromeThemeColor("#fff8e1", "#3a3228");
    return (
        <MuiMaterials.Box
            sx={{
                mx: 2,
                mt: 1,
                flexShrink: 0,
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${border}`,
                backgroundColor: bannerBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                color: textSecondary,
            }}
        >
            <MuiMaterials.Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1, m: 0 }}
            >
                {status}
            </MuiMaterials.Typography>
            {onDismiss ? (
                <MuiMaterials.Button
                    size="small"
                    variant="outlined"
                    onClick={onDismiss}
                    sx={{ flexShrink: 0, minWidth: 56, ...outlinedActionButtonSx() }}
                >
                    OK
                </MuiMaterials.Button>
            ) : null}
        </MuiMaterials.Box>
    );
}
